import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Search, X, ArrowUpRight } from 'lucide-react';
import { SEARCH_INDEX } from '@/data/content';

const SearchOverlay = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SEARCH_INDEX.slice(0, 6);
    return SEARCH_INDEX.filter(
      (r) => r.title.toLowerCase().includes(q) || r.excerpt.toLowerCase().includes(q) || r.type.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [query]);

  const go = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-navy/95 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-label="Search"
        >
          <div className="mx-auto max-w-3xl px-4 pt-24 sm:pt-32">
            <button data-testid="search-close" onClick={onClose} aria-label="Close search" className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-full border border-cream/20 text-cream hover:bg-cream/10 transition-colors">
              <X className="h-5 w-5" />
            </button>
            <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
              <div className="flex items-center gap-4 border-b-2 border-gold pb-4">
                <Search className="h-6 w-6 text-gold shrink-0" />
                <input
                  data-testid="search-input"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search notices, events, pages..."
                  className="w-full bg-transparent font-display text-2xl sm:text-4xl text-cream placeholder:text-cream/30 focus:outline-none"
                />
              </div>
              <div className="mt-6 space-y-2" data-testid="search-results">
                {results.length === 0 && <p className="text-cream/50 py-6">No results for “{query}”. Try “admission”, “exam” or “transport”.</p>}
                {results.map((r, i) => (
                  <button
                    key={`${r.title}-${i}`}
                    data-testid={`search-result-${i}`}
                    onClick={() => go(r.path)}
                    className="group flex w-full items-center justify-between gap-4 rounded-lg px-4 py-3 text-left transition-colors hover:bg-cream/10"
                  >
                    <span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold">{r.type}</span>
                      <span className="block font-semibold text-cream">{r.title}</span>
                      <span className="block text-sm text-cream/50 line-clamp-1">{r.excerpt}</span>
                    </span>
                    <ArrowUpRight className="h-4 w-4 text-cream/40 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-gold shrink-0" />
                  </button>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SearchOverlay;
